import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Badge } from '@capawesome/capacitor-badge';
import { distinctUntilChanged, firstValueFrom, Subscription } from 'rxjs';
import {
  RefreshNotificationMetricsAction,
  NotificationInboxSelectors,
} from '@smatchy/core';
import { getNotificationBadgeCount } from '../utils/notification-badge.util';

@Injectable({
  providedIn: 'root',
})
export class AppIconBadgeService {
  private readonly store = inject(Store);
  private subscription: Subscription;
  private isSupported = false;
  private hasInitialized = false;

  async start(): Promise<void> {
    if (this.hasInitialized || !Capacitor.isNativePlatform()) {
      return;
    }
    await this.initialize();
    await this.fetchAndApplyBadge();
    this.subscribeToUnreadCount();
    this.refreshOnResume();
  }

  async syncFromBackend(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    if (!this.hasInitialized) {
      await this.initialize();
    }
    await this.fetchAndApplyBadge();
  }

  async setBadge(count: number): Promise<void> {
    if (!this.isSupported) {
      return;
    }
    const safeCount = getNotificationBadgeCount(count);
    if (safeCount <= 0) {
      await Badge.clear();
      return;
    }
    await Badge.set({ count: safeCount });
  }

  async clearBadge(): Promise<void> {
    if (!this.isSupported) {
      return;
    }
    await Badge.clear();
  }

  stop(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.hasInitialized = false;
    this.isSupported = false;
  }

  protected async initialize(): Promise<void> {
    this.hasInitialized = true;
    this.isSupported = await this.ensureSupported();
  }

  protected async fetchAndApplyBadge(): Promise<void> {
    await firstValueFrom(
      this.store.dispatch(new RefreshNotificationMetricsAction()),
    );
    const count = this.store.selectSnapshot(
      NotificationInboxSelectors.unreadCount,
    );
    await this.setBadge(count ?? 0);
  }

  protected refreshOnResume(): void {
    App.addListener('resume', () => {
      this.syncFromBackend().catch((error) => {
        console.error('unableToSyncAppIconBadgeOnResume', error);
      });
    });
  }

  protected subscribeToUnreadCount(): void {
    this.subscription = this.store
      .select(NotificationInboxSelectors.unreadCount)
      .pipe(distinctUntilChanged())
      .subscribe((count) => {
        this.setBadge(count ?? 0).catch((error) => {
          console.error('unableToSetAppIconBadge', error);
        });
      });
  }

  protected async ensureSupported(): Promise<boolean> {
    try {
      const supported = await Badge.isSupported();
      if (!supported.isSupported) {
        return false;
      }
      if (Capacitor.getPlatform() === 'android') {
        return true;
      }
      const permission = await Badge.checkPermissions();
      if (permission.display === 'granted') {
        return true;
      }
      const requested = await Badge.requestPermissions();
      return requested.display === 'granted';
    } catch (error) {
      console.error('appIconBadgeNotSupported', error);
      return false;
    }
  }
}
